import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Project } from "../../types/projectTypes";
import { getActiveProjects } from "../../api/projectApi";
import { getUserProject } from "../../api/userApi";
import { profiles } from "../../contexts/constants/profiles";
import moment from "moment";
import { getMonthlySummary } from "../../api/monthlySummaryApi";
import { MonthlySummaryRow } from "../../types/monthlySummaryTypes";
import MonthlySummaryForm from "../../components/MonthlySummaryForm";
import MonthlySummaryTable from "../../components/MonthlySummaryTable";
import { DataChart } from "../../types/rasTypes";
import ExcelJS from "exceljs";
import logo from "../../assets/images/logoElecnor.png";
import Loader from "../../components/Loader";
import {
  Chart as Chartjs,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

Chartjs.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

/**
 * Informe "Resumen Mensual".
 *
 * Misma estructura que el Resumen Semanal, con una diferencia clave en el periodo:
 * aquí no se elige una semana sino una **fecha de corte**, y el informe acumula
 * desde el día 1 de ese mes hasta esa fecha. El backend deriva el rango; el
 * frontend solo envía la fecha y usa el periodo que le devuelven para rotular.
 */
const MonthlySummary: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);

  const [reportRows, setReportRows] = useState<MonthlySummaryRow[]>([]);
  const [period, setPeriod] = useState<{ start: string; end: string } | null>(null);
  const [chartData, setChartData] = useState<DataChart>({ labels: [], datasets: [] });

  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState({ show: false, message: "" });

  const [stage, setStage] = useState<string>("Todas");
  const [activityFilter, setActivityFilter] = useState<string>("Todas");
  const [uniqueSections, setUniqueSections] = useState<string[]>([]);
  const [uniqueActivities, setUniqueActivities] = useState<string[]>([]);
  const [isSelectedStage, setIsSelectedStage] = useState<boolean>(false);

  // Resalta en el calendario los días del periodo que se va a consultar:
  // del 1 del mes a la fecha elegida.
  const highlightMonthToDate = (date: Date) => {
    const cutoff = moment(date);
    const start = cutoff.clone().startOf("month");
    const dates: Date[] = [];
    for (const day = start; day.isSameOrBefore(cutoff, "day"); day.add(1, "days")) {
      dates.push(day.toDate());
    }
    setHighlightedDates(dates);
  };

  useEffect(() => {
    const loadProjects = async () => {
      setLoading(true);
      try {
        const userStr = localStorage.getItem("user");
        let rut = "", role = "";
        if (userStr) {
          const user = JSON.parse(userStr);
          rut = user.identificationNum;
          role = user.rols?.description;
        }
        let projectsData: Project[] = [];
        if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name) {
          projectsData = await getActiveProjects();
        } else {
          const userProjects = await getUserProject(rut);
          projectsData = Array.isArray(userProjects) ? userProjects : [];
        }
        setProjects(projectsData.filter((p) => p.status === undefined || p.status === true));
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    loadProjects();

    // Por defecto se ofrece el mes en curso hasta hoy.
    highlightMonthToDate(new Date());
  }, []);

  const handleProject = (e: SelectChangeEvent<number>) => {
    setSelectedProject(Number(e.target.value));
    setReportRows([]);
    setPeriod(null);
    setStage("Todas");
    setActivityFilter("Todas");
    setIsSelectedStage(false);
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    setSelectedDate(date);
    highlightMonthToDate(date);
  };

  const handleStage = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    setStage(val);
    if (val !== "Todas") {
      setIsSelectedStage(true);
      setUniqueActivities(Array.from(new Set(reportRows.filter(r => r.section === val).map(r => r.name))));
    } else {
      setIsSelectedStage(false);
    }
    setActivityFilter("Todas");
  };

  const handleActivity = (e: SelectChangeEvent<string>) => setActivityFilter(e.target.value);

  const onClickFilter = async () => {
    if (!selectedProject || !selectedDate) {
      setShowModal({ show: true, message: "Seleccione proyecto y fecha." });
      return;
    }
    setLoading(true);
    try {
      const date = moment(selectedDate).format("YYYY-MM-DD");
      const response = await getMonthlySummary(selectedProject, date);

      setReportRows(response.rows);
      setPeriod({ start: response.periodStart, end: response.periodEnd });
      setUniqueSections(Array.from(new Set(response.rows.map(r => r.section))));
      updateChart(response.rows);
    } catch (error) {
      console.error(error);
      setShowModal({ show: true, message: "Error al cargar datos." });
    } finally {
      setLoading(false);
    }
  };

  const updateChart = (rows: MonthlySummaryRow[]) => {
    const labels = rows.map(r => `${r.name} ${r.subActivity ? `(${r.subActivity})` : ""}`);
    const dataProgress = rows.map(r => Math.round(r.activityProgress || 0));
    const dataProgrammed = rows.map(r => r.totalScope > 0 ? Math.round((r.programmedAdvance / r.totalScope) * 100) : 0);

    setChartData({
      labels,
      datasets: [
        {
          type: "bar",
          label: "Total avance a la fecha (%)",
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          data: dataProgress,
          stack: "Stack 0",
        },
        {
          type: "line",
          label: "Total programado en el mes (%)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 2,
          fill: false,
          data: dataProgrammed,
        },
      ],
    });
  };

  // Filtrado visual (cliente) por etapa y actividad
  const getVisibleRows = () => reportRows.filter(row => {
    if (stage !== "Todas" && row.section !== stage) return false;
    if (activityFilter !== "Todas" && row.name !== activityFilter) return false;
    return true;
  });

  useEffect(() => {
    if (reportRows.length > 0) updateChart(getVisibleRows());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, activityFilter, reportRows]);

  const periodLabel = period
    ? `${moment(period.start).format("DD-MM-YYYY")} al ${moment(period.end).format("DD-MM-YYYY")}`
    : "";

  const onClickExportReport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Resumen Mensual");

    try {
      const imageResponse = await fetch(logo);
      const imageBuffer = await imageResponse.arrayBuffer();
      const imageId = workbook.addImage({ buffer: imageBuffer, extension: "png" });
      worksheet.addImage(imageId, { tl: { col: 0, row: 1 }, ext: { width: 160, height: 60 } });
    } catch (e) {
      console.warn("No se pudo cargar logo");
    }

    const project = projects.find((p) => p.id === selectedProject);
    if (project) {
      const cell = worksheet.getCell("D2");
      cell.value = `PROYECTO: ${project.name?.toUpperCase() || ""}`;
      cell.font = { bold: true, size: 18 };
    }
    worksheet.getCell("D3").value = `PERIODO: ${periodLabel}`;

    const startRow = 6;
    worksheet.getRow(startRow).values = [
      "Etapa", "Actividad", "Subcontrato", "Fecha Finalización Inicial", "Unidad", "Cantidad",
      "Acumulado anterior", "Acumulado Programa Inicial", "Rendimiento Oferta",
      "Desvío Programa Inicial", "Rendimiento Acumulado", "Ejecutadas en el periodo",
      "Rendimiento en el Periodo", "Acumulado Actual", "Avance a la fecha",
      "Programado en el periodo", "Desviación en el periodo", "Núm. Cuadrillas",
      "Plan M+1", "Rendimiento Pendiente/Estimado", "Nva. Fecha Finalización",
    ];
    worksheet.getRow(startRow).font = { bold: true };

    // Redondea a 2 decimales dejando el valor como número para Excel.
    const round2 = (value: number | string | undefined | null): number => {
      const num = Number(value);
      if (value === undefined || value === null || isNaN(num)) return 0;
      return Math.round(num * 100) / 100;
    };
    // Las columnas sin dato se exportan como "—" y no como 0, igual que en pantalla.
    const optional = (value: number | null | undefined): number | string =>
      value === null || value === undefined || isNaN(Number(value)) ? "—" : round2(value);

    getVisibleRows().forEach((row) => {
      const r = worksheet.addRow([
        row.section,
        `${row.name} ${row.subActivity ? `(${row.subActivity})` : ""}`,
        row.subcontract ?? "—",
        row.initialFinishDate ?? "—",
        row.unit,
        round2(row.totalScope),
        round2(row.previousExecuted),
        optional(row.initialProgramAccum),
        optional(row.offerYield),
        optional(row.initialProgramDeviation),
        optional(row.accumulatedYield),
        round2(row.currentExecuted),
        optional(row.periodYield),
        round2(row.currentAccumulation),
        round2(row.activityProgress),
        round2(row.programmedAdvance),
        round2(row.periodDeviation),
        round2(row.executedCrews),
        optional(row.planNextMonth),
        optional(row.pendingYield),
        row.newFinishDate ?? "—",
      ]);
      r.eachCell((cell) => { cell.alignment = { horizontal: "center", vertical: "middle" }; });
    });

    worksheet.columns.forEach((column) => { column.width = 18; });
    worksheet.getColumn(2).width = 40;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `resumen_mensual_${period?.end ?? moment(selectedDate).format("YYYY-MM-DD")}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3, height: "100%" }}>
      <Typography variant="h4" marginBottom={1}>Resumen Mensual</Typography>
      {periodLabel && (
        <Typography variant="subtitle2" color="text.secondary" marginBottom={4}>
          Periodo acumulado: {periodLabel}
        </Typography>
      )}

      {showModal.show && (
        <Dialog open={showModal.show} onClose={() => setShowModal({ ...showModal, show: false })}>
          <DialogTitle>{showModal.message}</DialogTitle>
          <DialogActions>
            <Button onClick={() => setShowModal({ ...showModal, show: false })}>Aceptar</Button>
          </DialogActions>
        </Dialog>
      )}

      {loading && <Loader />}

      <Box sx={{ p: 3 }}>
        <MonthlySummaryForm
          projects={projects}
          selectedProject={selectedProject}
          selectedDate={selectedDate}
          stage={stage}
          activity={activityFilter}
          highlightedDates={highlightedDates}
          uniqueSections={uniqueSections}
          uniqueActivities={uniqueActivities}
          isSelectedStage={isSelectedStage}
          handleProject={handleProject}
          handleDateChange={handleDateChange}
          handleStage={handleStage}
          handleActivity={handleActivity}
          onClickFilter={onClickFilter}
        />

        <Grid style={{ marginTop: 50 }}>
          <MonthlySummaryTable rows={getVisibleRows()} />
        </Grid>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="contained" disabled={reportRows.length === 0} onClick={onClickExportReport}>
            Exportar informe
          </Button>
        </Box>

        {reportRows.length > 0 && (
          <Box sx={{ width: "100%", mt: 10, display: "flex", justifyContent: "center" }}>
            <Bar data={chartData as any} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MonthlySummary;
